import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import CartAPI from '../utils/cartAPI';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartTotals, setCartTotals] = useState(null);
  const { user, isAuthenticated } = useAuth();

  const loadCart = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await CartAPI.getCart();
      // Normalize backend format to frontend format
      const normalizedItems = (data.items || []).map(item => ({
        id: item.product_id,
        name: item.product_name,
        price: item.product_price,
        image: item.selected_image_url || item.product_image,
        quantity: item.quantity,
        selected_image_url: item.selected_image_url || null,
        extras: Array.isArray(item.extras) ? item.extras : [],
      }));
      setCartItems(normalizedItems);
      setCartTotals({
        subtotal_products: data.subtotal_products,
        subtotal_extras: data.subtotal_extras,
        subtotal: data.subtotal,
        shipping_cost: data.shipping_cost,
        total: data.total,
      });
    } catch (error) {
      console.error('Failed to load cart:', error);
      if (error.message === 'NOT_AUTHENTICATED') {
        setCartItems([]);
        setCartTotals(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load cart from backend when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadCart();
    } else {
      setCartItems([]);
      setCartTotals(null);
    }
  }, [isAuthenticated, loadCart, user?.id]);

  const addToCart = async (product, quantity = 1, options = {}) => {
    // Check authentication
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      const currentPath = window.location.pathname;
      setTimeout(() => {
        window.location.href = `/giris?returnUrl=${encodeURIComponent(currentPath)}`;
      }, 500);
      return;
    }

    try {
      setLoading(true);
      const res = await CartAPI.addItem(product, quantity, options);

      const item = res?.item;
      if (item?.product_id) {
        setCartItems((prev) => {
          const pid = String(item.product_id);
          const idx = prev.findIndex((x) => String(x.id) === pid);
          const mapped = {
            id: pid,
            name: item.product_name,
            price: item.product_price,
            image: item.selected_image_url || item.product_image,
            quantity: item.quantity,
            selected_image_url: item.selected_image_url || null,
            extras: Array.isArray(item.extras) ? item.extras : [],
          };
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...mapped };
            return next;
          }
          return [mapped, ...prev];
        });
      }

      await loadCart();

      toast.success('Ürün sepete eklendi');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      if (error.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        window.location.href = '/giris';
      } else {
        toast.error('Sepete eklenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      await CartAPI.removeItem(productId);
      setCartItems((prev) => prev.filter((x) => String(x.id) !== String(productId)));
      await loadCart();
      toast.success('Ürün sepetten çıkarıldı');
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      toast.error('Sepetten çıkarılırken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      if (quantity < 1) {
        await removeFromCart(productId);
      } else {
        await CartAPI.updateQuantity(productId, quantity);
        setCartItems((prev) =>
          prev.map((x) => (String(x.id) === String(productId) ? { ...x, quantity } : x))
        );
        await loadCart();
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
      toast.error('Miktar güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateExtraQuantity = async (productId, extraId, quantity) => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      await CartAPI.updateExtraQuantity(productId, extraId, quantity);
      await loadCart();
    } catch (error) {
      console.error('Failed to update extra quantity:', error);
      if (error.message === 'NOT_AUTHENTICATED') {
        toast.error('Lütfen giriş yapın');
        window.location.href = '/giris';
      } else {
        toast.error('Ekstra güncellenirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      toast.info('Lütfen giriş yapın');
      window.location.href = '/giris';
      return;
    }

    try {
      setLoading(true);
      await CartAPI.clearCart();
      setCartItems([]);
      setCartTotals(null);
      toast.success('Sepet temizlendi');
    } catch (error) {
      console.error('Failed to clear cart:', error);
      toast.error('Sepet temizlenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getCartTotal = () => {
    const v = cartTotals?.subtotal;
    if (typeof v === 'number') return v;
    return 0;
  };

  const getShippingCost = () => {
    const v = cartTotals?.shipping_cost;
    if (typeof v === 'number') return v;
    return 0;
  };

  const getExtrasTotal = () => {
    const v = cartTotals?.subtotal_extras;
    if (typeof v === 'number') return v;
    return 0;
  };

  const getGrandTotal = () => {
    const v = cartTotals?.total;
    if (typeof v === 'number') return v;
    return 0;
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + (item.quantity || 0), 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateExtraQuantity,
        clearCart,
        getCartTotal,
        getExtrasTotal,
        getShippingCost,
        getGrandTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
